import React, { useState, useEffect, useCallback } from "react";
import { RevoltModal, RevoltTable, RevoltTableCell, RevoltSwitch } from "../../../components";
import DropdownSelect from "../../../components/DropdownSelect";
import "./ModelsModal.css";
import "./LocalModelsModal.css";

function pickFirstValue(...values) {
  for (const v of values) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim().length === 0) continue;
    return v;
  }
  return null;
}

function getOverviewFromMeta(ggufMeta) {
  if (!ggufMeta) return { publisher: null, displayName: null, architecture: null, contextLength: null };
  const general = ggufMeta?.metadata?.general || null;
  const baseModel0 = general?.base_model?.["0"] ?? general?.base_model?.[0] ?? null;
  const architecture = general?.architecture ?? null;
  const publisher = pickFirstValue(baseModel0?.organization, general?.organization, general?.publisher);
  const displayName = pickFirstValue(baseModel0?.name, general?.name);
  let contextLength = null;
  if (architecture === "llama" && ggufMeta?.metadata?.llama) contextLength = ggufMeta.metadata.llama.context_length ?? null;
  else if (architecture === "gpt-oss" && ggufMeta?.metadata?.["gpt-oss"]) contextLength = ggufMeta.metadata["gpt-oss"].context_length ?? null;
  else if (architecture === "qwen3" && ggufMeta?.metadata?.qwen3) contextLength = ggufMeta.metadata.qwen3.context_length ?? null;
  else if (architecture === "qwen35" && ggufMeta?.metadata?.qwen35) contextLength = ggufMeta.metadata.qwen35.context_length ?? null;
  return { publisher, displayName, architecture, contextLength };
}

export function LocalModelsModal({ isOpen, onClose }) {
  const [models, setModels] = useState([]);
  const [insightsMap, setInsightsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  // Sorting state - default: enabled first (desc), so enabled models appear at top
  const [sortBy, setSortBy] = useState("enabled"); // 'none', 'name', 'provider', 'architecture', 'context', 'enabled'
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc', 'desc'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadModels = useCallback(async () => {
    if (typeof window === "undefined" || !window.modelsAPI?.getAll) return;
    setLoading(true);
    try {
      const list = await window.modelsAPI.getAll();
      setModels(list || []);
      const map = {};
      (list || []).forEach((m) => {
        if (m?.metadata?.insightsOverview) map[m.id] = m.metadata.insightsOverview;
      });
      const missing = (list || []).filter((m) => m.path && !map[m.id]);
      if (window.llamaCppAPI?.getInsights && window.modelsAPI?.setInsightsCache && missing.length > 0) {
        await Promise.all(
          missing.map(async (m) => {
            try {
              const result = await window.llamaCppAPI.getInsights(m.path);
              const meta = result?.success ? result.metadata : null;
              const overview = getOverviewFromMeta(meta) || {};
              map[m.id] = overview;
              if (Object.keys(overview).length > 0) {
                try { await window.modelsAPI.setInsightsCache(m.id, overview); } catch (_) { /* ignore */ }
              }
            } catch (_) {
              map[m.id] = {};
            }
          })
        );
      }
      setInsightsMap(map);
    } catch (_) {
      setModels([]);
      setInsightsMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadModels();
      // Reset sort and pagination to defaults each time modal opens
      setSortBy("enabled");
      setSortDirection("desc");
      setCurrentPage(1);
    }
  }, [isOpen, loadModels]);

  // Filter and sort models
  const getFilteredModels = useCallback(() => {
    let filtered = models.filter((m) => {
      if (!searchText.trim()) return true;
      const search = searchText.toLowerCase();
      const name = (m.displayName || m.name || m.id || "").toLowerCase();
      const provider = (insightsMap[m.id]?.publisher || "").toLowerCase();
      const arch = (insightsMap[m.id]?.architecture || "").toLowerCase();
      return name.includes(search) || provider.includes(search) || arch.includes(search);
    });

    if (sortBy !== "none") {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        const insightsA = insightsMap[a.id] || {};
        const insightsB = insightsMap[b.id] || {};

        switch (sortBy) {
          case "name":
            aValue = (a.displayName || a.name || a.id || "").toLowerCase();
            bValue = (b.displayName || b.name || b.id || "").toLowerCase();
            break;
          case "provider":
            aValue = (insightsA.publisher || "").toLowerCase();
            bValue = (insightsB.publisher || "").toLowerCase();
            break;
          case "architecture":
            aValue = (insightsA.architecture || "").toLowerCase();
            bValue = (insightsB.architecture || "").toLowerCase();
            break;
          case "context":
            aValue = insightsA.contextLength != null ? Number(insightsA.contextLength) : -1;
            bValue = insightsB.contextLength != null ? Number(insightsB.contextLength) : -1;
            break;
          case "enabled":
            aValue = a.isActive ? 1 : 0;
            bValue = b.isActive ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [models, insightsMap, searchText, sortBy, sortDirection]);

  const filteredModels = getFilteredModels();
  const totalPages = Math.ceil(filteredModels.length / pageSize);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, sortBy, sortDirection]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(Number(size));
    setCurrentPage(1);
  };

  const handleToggleActive = async (modelId) => {
    const model = models.find((m) => m.id === modelId);
    if (!model || togglingId) return;
    setTogglingId(modelId);
    try {
      await window.modelsAPI.update(modelId, { isActive: !model.isActive });
      setModels((prev) =>
        prev.map((m) => (m.id === modelId ? { ...m, isActive: !m.isActive } : m))
      );
    } catch (_) {
      // keep state on error
    } finally {
      setTogglingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="local-models-modal-title">
          <i className="fa-solid fa-microchip" aria-hidden />
          {" "}
          Local Models
        </span>
      }
      containerClassName="modal-wide local-models-modal-container"
    >
      <div className="local-models-modal-content">
        <div className="models-controls">
          <div className="controls-row">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search by name or provider..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="models-search-input"
              />
            </div>
            <div className="sort-by-group">
              <DropdownSelect
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value);
                  setSortDirection(value === "enabled" ? "desc" : "asc");
                }}
                options={[
                  { value: "none", label: "No Sorting" },
                  { value: "name", label: "Name" },
                  { value: "provider", label: "Provider" },
                  { value: "architecture", label: "Architecture" },
                  { value: "context", label: "Context Length" },
                  { value: "enabled", label: "Enabled" },
                ]}
                placeholder="Sort by..."
                className="sort-select"
              />
            </div>
            {sortBy !== "none" && (
              <div className="sort-direction-group">
                <button
                  className={`direction-toggle ${sortDirection === "asc" ? "active" : ""}`}
                  onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                  title={sortDirection === "asc" ? "Ascending" : "Descending"}
                >
                  {sortDirection === "asc" ? "↑" : "↓"}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="local-models-table-container">
          {loading ? (
            <div className="local-models-loading">Loading local models...</div>
          ) : (
            <RevoltTable
              className="scrollable"
              headers={["Name", "Provider", "Architecture", "Context Length", "Enabled"]}
              gridColumns={[1.2, 0.8, 0.6, 0.5, 0.35]}
              emptyMessage="No local models found. Add models in Desktop Settings → Models."
              rows={filteredModels
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((m) => {
                const insights = insightsMap[m.id] || {};
                const name = m.displayName || m.name || m.id;
                const provider = insights.publisher || "—";
                const arch = insights.architecture || "—";
                const ctx = insights.contextLength != null ? Number(insights.contextLength).toLocaleString() : "—";
                return [
                  <span className="local-models-name-cell" title={name}>
                    {name}
                  </span>,
                  <span className="local-models-provider-cell" title={provider}>
                    {provider}
                  </span>,
                  <span className="local-models-arch-cell" title={arch}>
                    {arch}
                  </span>,
                  <span className="local-models-ctx-cell" title={ctx}>
                    {ctx}
                  </span>,
                  <RevoltTableCell className="local-models-enabled-cell">
                    <div className="local-models-switch-wrap">
                      <RevoltSwitch
                        checked={!!m.isActive}
                        onChange={() => handleToggleActive(m.id)}
                        disabled={togglingId === m.id}
                      />
                    </div>
                  </RevoltTableCell>,
                ];
              })}
            />
          )}
        </div>
        {!loading && (
          <div className="models-pagination">
            <div className="page-size">
              <span>Show</span>
              <DropdownSelect
                value={pageSize}
                options={[10, 20, 50, 100]}
                onValueChange={(val) => handlePageSizeChange(Number(val))}
              />
              <span>per page</span>
            </div>
            <div className="models-stats">
              <span className="models-count">
                {filteredModels.length} of {models.length} model{models.length !== 1 ? "s" : ""}
                {searchText.trim() ? " (filtered)" : ""}
              </span>
            </div>
            {filteredModels.length > pageSize && (
              <div className="page-controls">
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                >
                  <i className="fa-solid fa-chevron-left" />
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                >
                  <i className="fa-solid fa-chevron-right" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </RevoltModal>
  );
}
